import merge from 'lodash/merge';
import moment from 'moment';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { fetchCurrentUser, currentUserShowSuccess } from '../../ducks/user.duck';

export const SAVE_CONTACT_DETAILS_REQUEST = 'app/ContactDetailsPage/SAVE_CONTACT_DETAILS_REQUEST';
export const SAVE_CONTACT_DETAILS_SUCCESS = 'app/ContactDetailsPage/SAVE_CONTACT_DETAILS_SUCCESS';
export const SAVE_CONTACT_DETAILS_ERROR = 'app/ContactDetailsPage/SAVE_CONTACT_DETAILS_ERROR';
export const SAVE_EMAIL_ERROR = 'app/ContactDetailsPage/SAVE_EMAIL_ERROR';
export const SAVE_PHONE_NUMBER_ERROR = 'app/ContactDetailsPage/SAVE_PHONE_NUMBER_ERROR';
export const SAVE_CONTACT_DETAILS_CLEAR = 'app/ContactDetailsPage/SAVE_CONTACT_DETAILS_CLEAR';

export const RESET_PASSWORD_REQUEST = 'app/ContactDetailsPage/RESET_PASSWORD_REQUEST';
export const RESET_PASSWORD_SUCCESS = 'app/ContactDetailsPage/RESET_PASSWORD_SUCCESS';
export const RESET_PASSWORD_ERROR = 'app/ContactDetailsPage/RESET_PASSWORD_ERROR';

const initialState = {
  saveContactDetailsError: null,
  saveEmailError: null,
  savePhoneNumberError: null,
  saveContactDetailsInProgress: false,
  contactDetailsChanged: false,
  resetPasswordInProgress: false,
  resetPasswordError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SAVE_CONTACT_DETAILS_REQUEST:
      return {
        ...state,
        saveContactDetailsInProgress: true,
        saveContactDetailsError: null,
        saveEmailError: null,
        savePhoneNumberError: null,
        contactDetailsChanged: false,
      };
    case SAVE_CONTACT_DETAILS_SUCCESS:
      return { ...state, saveContactDetailsInProgress: false, contactDetailsChanged: true };
    case SAVE_CONTACT_DETAILS_ERROR:
      return { ...state, saveContactDetailsInProgress: false, saveContactDetailsError: payload };
    case SAVE_EMAIL_ERROR:
      return { ...state, saveContactDetailsInProgress: false, saveEmailError: payload };
    case SAVE_PHONE_NUMBER_ERROR:
      return { ...state, saveContactDetailsInProgress: false, savePhoneNumberError: payload };
    case SAVE_CONTACT_DETAILS_CLEAR:
      return {
        ...state,
        saveContactDetailsInProgress: false,
        saveContactDetailsError: null,
        saveEmailError: null,
        savePhoneNumberError: null,
        contactDetailsChanged: false,
      };
    case RESET_PASSWORD_REQUEST:
      return { ...state, resetPasswordInProgress: true, resetPasswordError: null };
    case RESET_PASSWORD_SUCCESS:
      return { ...state, resetPasswordInProgress: false };
    case RESET_PASSWORD_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, resetPasswordInProgress: false, resetPasswordError: payload };
    default:
      return state;
  }
}

export const saveContactDetailsRequest = () => ({ type: SAVE_CONTACT_DETAILS_REQUEST });
export const saveContactDetailsSuccess = () => ({ type: SAVE_CONTACT_DETAILS_SUCCESS });
export const saveContactDetailsError = error => ({
  type: SAVE_CONTACT_DETAILS_ERROR,
  payload: error,
  error: true,
});
export const saveEmailError = error => ({
  type: SAVE_EMAIL_ERROR,
  payload: error,
  error: true,
});
export const savePhoneNumberError = error => ({
  type: SAVE_PHONE_NUMBER_ERROR,
  payload: error,
  error: true,
});
export const saveContactDetailsClear = () => ({ type: SAVE_CONTACT_DETAILS_CLEAR });
export const resetPasswordRequest = () => ({ type: RESET_PASSWORD_REQUEST });
export const resetPasswordSuccess = () => ({ type: RESET_PASSWORD_SUCCESS });
export const resetPasswordError = e => ({
  type: RESET_PASSWORD_ERROR,
  error: true,
  payload: e,
});

const profileQueryParams = {
  expand: true,
  include: ['profileImage'],
  'fields.image': ['variants.square-small', 'variants.square-small2x'],
};

const requestSaveEmail = params => (dispatch, getState, sdk) => {
  const { email, currentPassword } = params;

  return sdk.currentUser
    .changeEmail({ email, currentPassword }, profileQueryParams)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.changeEmail response');
      }

      return entities[0];
    })
    .catch(e => {
      dispatch(saveEmailError(storableError(e)));
      throw e;
    });
};

const requestUpdateContactProfile = params => (dispatch, getState, sdk) => {
  return sdk.currentUser
    .updateProfile(params, profileQueryParams)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }

      return entities[0];
    })
    .catch(e => {
      dispatch(saveContactDetailsError(storableError(e)));
      throw e;
    });
};

export const saveContactDetails = params => dispatch => {
  dispatch(saveContactDetailsRequest());

  const {
    email,
    currentEmail,
    currentPhoneNumber: _currentPhoneNumber,
    currentPassword,
    phoneNumber,
    firstName,
    lastName,
    dob,
    ...rest
  } = params;
  const emailChanged = email !== currentEmail;

  return Promise.resolve()
    .then(async () => {
      const emailUser = emailChanged
        ? await dispatch(requestSaveEmail({ email, currentPassword }))
        : null;

      if (emailUser) {
        dispatch(currentUserShowSuccess(emailUser));
      }

      const updatedProfileUser = await dispatch(
        requestUpdateContactProfile({
          firstName,
          lastName,
          protectedData: {
            dob: dob ? moment(dob).format('YYYY-MM-DD') : null,
            phoneNumber,
            ...rest,
          },
        })
      );

      const mergedCurrentUser = emailUser
        ? merge({}, emailUser, {
            attributes: {
              profile: updatedProfileUser.attributes.profile,
            },
          })
        : updatedProfileUser;

      dispatch(currentUserShowSuccess(mergedCurrentUser));
      dispatch(saveContactDetailsSuccess());

      return mergedCurrentUser;
    })
    .catch(() => null);
};

export const resetPassword = email => (dispatch, getState, sdk) => {
  dispatch(resetPasswordRequest());
  return sdk.passwordReset
    .request({ email })
    .then(() => dispatch(resetPasswordSuccess()))
    .catch(e => dispatch(resetPasswordError(storableError(e))));
};

export const loadData = () => {
  return fetchCurrentUser();
};
